/**
 * print/printFormats — region-agnostic print format tokens.
 *
 * Every print artifact in EventDrop is anchored to a PrintFormat. A
 * format declares the dimensions of the *trim* (the final cut piece
 * a guest holds), plus bleed and safe margins around it, plus a
 * canonical DPI for server-side raster output. Region (UK A-series
 * vs US imperial) is just metadata — the geometry layer translates
 * everything to millimetres so the rest of the system never has to
 * know which side of the Atlantic the print partner sits on.
 *
 * Adding a new format:
 *   • declare trim_mm: [width, height]
 *   • bleed_mm: outward extension beyond the trim, all four sides
 *   • safe_mm: inward inset where no content should be cropped
 *   • dpi: 300 for press, 600 for fine-art
 *   • aspect_label: human-readable "50×150 mm strip", "11×17″ poster"
 *
 * IDs are constants, never localised — backend, frontend and the
 * (future) print-partner adapter share the same vocabulary.
 */

const IN_TO_MM = 25.4;
const inches = (v) => +(v * IN_TO_MM).toFixed(3);

export const PRINT_FORMATS = {
  // ── Polaroid strip — the canonical first artifact ───────────────
  POLAROID_STRIP_50x150: {
    id: 'POLAROID_STRIP_50x150',
    region: 'UK',
    label: 'Polaroid strip · 50 × 150 mm',
    family: 'polaroid_strip',
    trim_mm: [50, 150],
    bleed_mm: 3,
    safe_mm: 4,
    dpi: 300,
    orientation: 'portrait',
  },
  POLAROID_STRIP_2x6IN: {
    id: 'POLAROID_STRIP_2x6IN',
    region: 'US',
    label: 'Polaroid strip · 2 × 6 in',
    family: 'polaroid_strip',
    trim_mm: [inches(2), inches(6)],
    bleed_mm: inches(0.125),
    safe_mm: inches(0.18),
    dpi: 300,
    orientation: 'portrait',
  },

  // ── Posters ─────────────────────────────────────────────────────
  POSTER_A3_UK: {
    id: 'POSTER_A3_UK',
    region: 'UK',
    label: 'Poster · A3 (297 × 420 mm)',
    family: 'poster',
    trim_mm: [297, 420],
    bleed_mm: 3,
    safe_mm: 8,
    dpi: 300,
    orientation: 'portrait',
  },
  POSTER_11x17_US: {
    id: 'POSTER_11x17_US',
    region: 'US',
    label: 'Poster · 11 × 17 in',
    family: 'poster',
    trim_mm: [inches(11), inches(17)],
    bleed_mm: inches(0.125),
    safe_mm: inches(0.25),
    dpi: 300,
    orientation: 'portrait',
  },

  // ── Memory guestbook ────────────────────────────────────────────
  GUESTBOOK_A5_UK: {
    id: 'GUESTBOOK_A5_UK',
    region: 'UK',
    label: 'Memory guestbook · A5 (148 × 210 mm)',
    family: 'guestbook',
    trim_mm: [148, 210],
    bleed_mm: 3,
    safe_mm: 8,
    dpi: 300,
    orientation: 'portrait',
  },
  GUESTBOOK_5_5x8_5_US: {
    id: 'GUESTBOOK_5_5x8_5_US',
    region: 'US',
    label: 'Memory guestbook · 5.5 × 8.5 in',
    family: 'guestbook',
    trim_mm: [inches(5.5), inches(8.5)],
    bleed_mm: inches(0.125),
    safe_mm: inches(0.25),
    dpi: 300,
    orientation: 'portrait',
  },
};

// Default format per family — what the studio renders before the
// host has expressed a preference. Always the UK/A-series option
// for now; will become region-aware once the dashboard knows the
// host's billing locale.
export const DEFAULT_FORMAT_BY_FAMILY = {
  polaroid_strip: 'POLAROID_STRIP_50x150',
  poster: 'POSTER_A3_UK',
  guestbook: 'GUESTBOOK_A5_UK',
};

export function getFormat(idOrFormat) {
  if (!idOrFormat) return null;
  if (typeof idOrFormat === 'object' && idOrFormat.trim_mm) return idOrFormat;
  return PRINT_FORMATS[idOrFormat] || null;
}

export function formatsForFamily(family) {
  return Object.values(PRINT_FORMATS).filter((f) => f.family === family);
}
